import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import { useScreenSizeContext } from '../../../../../contexts/screen-size-resolution';
import { SellerSettings } from '../../../../../enums/seller-settings/seller-settings';
import { useModifiableSellerSetting } from '../../../../../hooks/use-modifiable-seller-setting';
import { useSellerFTUWizardJson } from '../../../../../hooks/use-seller-setting';
import { FTU_WIZARD_PATH, HOME_PAGE_PATH } from '../../../../../routes/constants';
import {
  normalizeTodosArray,
  getVisibleTodos,
  convertTodosForStorage,
  ensureDefaultTodosPresent,
} from '../../todo-cards/todo-normalization-utils';
import { FTU_WIZARD_STEPS } from '../constants';
import { isStepCompleted as isStepCompletedUtil, getStepperStepForWizardStep } from '../utils/step-mapping';

const FTUWizardContext = React.createContext({});
export const useFTUWizardContext = () => React.useContext(FTUWizardContext);
export const FTUWizardContextProvider = ({ children }) => {
  const history = useHistory();
  const { isMobile, isUltraMobile } = useScreenSizeContext();
  const isMobileOrSmaller = isMobile || isUltraMobile;
  const todosFromJson = useSellerFTUWizardJson().todos || emptyArray;
  const normalizedTodos = useMemo(() => normalizeTodosArray(todosFromJson), [todosFromJson]);
  const [todosInLocalState, setTodosInLocalState] = useState(normalizedTodos);

  const [wizardState, setWizardState] = useModifiableSellerSetting(SellerSettings.sellerFTUWizardJson, emptyObject);

  const triggerAutoInjection = useCallback(() => {
    let result = false;
    try {
      const todosWithDefaults = ensureDefaultTodosPresent(todosFromJson);

      if (todosWithDefaults.length > todosFromJson.length) {
        const normalizedWithDefaults = normalizeTodosArray(todosWithDefaults);
        setTodosInLocalState(normalizedWithDefaults);

        setWizardState((prevState) => ({
          ...prevState,
          todos: todosWithDefaults,
        }));

        result = true;
      }
    } catch {
      result = false;
    }
    return result;
  }, [todosFromJson, setWizardState]);

  // Sync local state when normalized todos change (e.g., after subscription plan change)
  useEffect(() => {
    setTodosInLocalState(normalizedTodos);
  }, [normalizedTodos]);

  const visibleTodos = useMemo(() => getVisibleTodos(todosInLocalState), [todosInLocalState]);
  const visibleTodoIds = useMemo(() => visibleTodos.map((todo) => todo.id), [visibleTodos]);
  const todoIdsInLocalState = useMemo(() => todosInLocalState.map((todo) => todo.id), [todosInLocalState]);

  const {
    lastVisitedPage,
    merchantSupportsReturns,
    isSetupWizardComplete,
    inventoryManagementOption: persistedInventoryManagementOption,
    selectedInitiationOptions: persistedInitiationOptions,
  } = wizardState;

  const updateTodosLocalOnly = useCallback(
    (updatedTodos) => {
      setTodosInLocalState(updatedTodos);
    },
    [setTodosInLocalState],
  );

  const updateTodosWithSave = useCallback(
    (updatedTodos) => {
      setTodosInLocalState(updatedTodos);
      const todosForStorage = convertTodosForStorage(updatedTodos);
      setWizardState((prevState) => ({
        ...prevState,
        todos: todosForStorage,
      }));
    },
    [setTodosInLocalState, setWizardState],
  );

  const updateFTUWizardSellerSetting = useCallback(
    (page, todos, merchantSupportsReturnsValue) => {
      setWizardState((prevState) => ({
        ...prevState,
        lastVisitedPage: page || prevState.lastVisitedPage,
        todos: todos ? convertTodosForStorage(todos) : prevState.todos,
        ...(merchantSupportsReturnsValue !== undefined && {
          merchantSupportsReturns: merchantSupportsReturnsValue,
        }),
      }));
    },
    [setWizardState],
  );

  const updateReturnsSelection = useCallback(
    (merchantSupportsReturnsValue) => {
      setWizardState((prevState) => ({
        ...prevState,
        merchantSupportsReturns: merchantSupportsReturnsValue,
      }));
    },
    [setWizardState],
  );

  const updateInventoryManagementOption = useCallback(
    (option) => {
      setWizardState((prevState) => ({
        ...prevState,
        inventoryManagementOption: option,
      }));
    },
    [setWizardState],
  );

  const updateInitiationOptions = useCallback(
    (options) => {
      setWizardState((prevState) => ({
        ...prevState,
        selectedInitiationOptions: options,
      }));
    },
    [setWizardState],
  );

  const markWizardComplete = useCallback(() => {
    setWizardState((prevState) => ({
      ...prevState,
      isSetupWizardComplete: true,
    }));
  }, [setWizardState]);

  const restartWizard = useCallback(async () => {
    const todosForStorage = convertTodosForStorage(todosInLocalState);
    await setWizardState((prevState) => ({
      ...prevState,
      lastVisitedPage: FTU_WIZARD_STEPS.ORDERS,
      todos: todosForStorage,
      isSetupWizardComplete: false,
    }));
  }, [setWizardState, todosInLocalState]);

  const onDismissSetupCompleteBanner = useCallback(() => {
    setWizardState((prevState) => ({
      ...prevState,
      isSetupCompleteBannerDismissed: true,
    }));
  }, [setWizardState]);

  const getInitialStep = useCallback(() => {
    if (!lastVisitedPage) {
      return FTU_WIZARD_STEPS.ORDERS;
    }

    const stepExists = Object.values(FTU_WIZARD_STEPS).includes(lastVisitedPage);
    return stepExists ? lastVisitedPage : FTU_WIZARD_STEPS.ORDERS;
  }, [lastVisitedPage]);

  const getStepKey = useCallback(
    (stepParam) => {
      if (!stepParam) {
        return getInitialStep();
      }

      const stepKey = Object.values(FTU_WIZARD_STEPS).find((key) => key === stepParam);
      return stepKey || getInitialStep();
    },
    [getInitialStep],
  );

  const handleClose = useCallback(() => {
    history.push(HOME_PAGE_PATH);
  }, [history]);

  // Calculate the current step number based on lastVisitedPage
  // Uses stepper mapping to show progress in terms of 4-step stepper display
  const getCurrentStepCount = useCallback(() => {
    if (!lastVisitedPage) {
      return 0;
    }
    const stepperStep = getStepperStepForWizardStep(lastVisitedPage);
    return stepperStep;
  }, [lastVisitedPage]);

  // Check if a specific step is completed
  const isStepCompleted = useCallback(
    (stepKey) => {
      if (!lastVisitedPage) {
        return false;
      }
      const completed = isStepCompletedUtil(stepKey, lastVisitedPage);
      return completed;
    },
    [lastVisitedPage],
  );

  const navigateToStep = useCallback(
    (stepKey) => {
      updateFTUWizardSellerSetting(stepKey, todosInLocalState);

      // Use replace instead of push to avoid history issues
      // And navigate to home first, then to the wizard to force a clean re-initialization
      history.replace(HOME_PAGE_PATH);

      Promise.resolve().then(() => {
        history.replace(`${FTU_WIZARD_PATH}/${stepKey}`);
      });
    },
    [updateFTUWizardSellerSetting, todosInLocalState, history],
  );

  const value = useMemo(
    () => ({
      todosInLocalState,
      setTodosInLocalState,
      updateTodosLocalOnly,
      updateTodosWithSave,
      todoIdsInLocalState,
      visibleTodos,
      visibleTodoIds,
      wizardState,
      isSetupWizardComplete,
      merchantSupportsReturns,
      updateFTUWizardSellerSetting,
      updateReturnsSelection,
      onDismissSetupCompleteBanner,
      getInitialStep,
      getStepKey,
      handleClose,
      getCurrentStepCount,
      isStepCompleted,
      isMobileOrSmaller,
      navigateToStep,
      markWizardComplete,
      restartWizard,
      triggerAutoInjection,
      persistedInventoryManagementOption,
      updateInventoryManagementOption,
      persistedInitiationOptions,
      updateInitiationOptions,
    }),
    [
      todosInLocalState,
      setTodosInLocalState,
      updateTodosLocalOnly,
      updateTodosWithSave,
      todoIdsInLocalState,
      visibleTodos,
      visibleTodoIds,
      wizardState,
      isSetupWizardComplete,
      merchantSupportsReturns,
      updateFTUWizardSellerSetting,
      updateReturnsSelection,
      onDismissSetupCompleteBanner,
      getInitialStep,
      getStepKey,
      handleClose,
      getCurrentStepCount,
      isStepCompleted,
      isMobileOrSmaller,
      navigateToStep,
      markWizardComplete,
      restartWizard,
      triggerAutoInjection,
      persistedInventoryManagementOption,
      updateInventoryManagementOption,
      persistedInitiationOptions,
      updateInitiationOptions,
    ],
  );

  return <FTUWizardContext.Provider value={value}>{children}</FTUWizardContext.Provider>;
};
